<template>
  <div class="car-signup-form bg-white-pearl">
    <!-- App Bar with Logo -->
    <v-app-bar class="app-bar" flat>
      <v-container class="logo-container">
        <v-img
          :src="require('@/assets/suuq.logo.svg')"
          alt="SUUQ Logo"
          class="logo-img"
          contain
          @click="navigateToHome"
        />
      </v-container>
    </v-app-bar>

    <!-- Main Content -->
    <v-container
      fluid
      style="max-width: 500px; width: 100%"
      class="content-container"
    >
      <!-- Title -->
      <v-card class="title-card" flat>
        <v-card-title class="title"
          ><div class="car-title"></div>
          Tell us about your car</v-card-title
        >

        <!-- Breadcrumbs -->
        <Breadcrumbs />
      </v-card>

      <!-- Selling More Than One Car Section -->
      <v-card class="info-card" flat>
        <v-card-text>
          <div class="info-title">Selling more than one car?</div>
          <div class="info-subtitle">
            Save with our business packages!
            <a href="#" class="learn-more">Learn More</a>
          </div>
        </v-card-text>
      </v-card>

      <!-- Form Fields -->
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row dense>
          <!-- Title Field -->
          <v-col cols="12">
            <v-text-field
              v-model="formData.title"
              label="Title *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
              placeholder="e.g. 2019 Toyota Camry in Excellent Condition"
            ></v-text-field>
          </v-col>

          <!-- City Field -->
          <v-col cols="12">
            <v-select
              v-model="formData.cities"
              :items="cities"
              item-title="name"
              item-value="slug"
              label="City *"
              :rules="[requiredRule]"
              variant="outlined"
              return-object
            ></v-select>
          </v-col>

          <!-- Make & Model -->
          <!-- <v-col cols="12">
            <v-select
              v-model="formData.makeModels"
              :items="makeModels"
              item-title="name"
              item-value="slug"
              label="Make & Model *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
              return-object
            ></v-select>
          </v-col> -->

          <v-col cols="12">
            <v-select
              v-model="formData.selectedGroup"
              :items="groups"
              item-title="name"
              item-value="id"
              label="Make & Model *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
              return-object
              @update:model-value="updateSectionsForSelectedGroup"
            ></v-select>
          </v-col>

          <!-- Trim -->
          <!-- <v-col cols="12">
            <v-select
              v-model="formData.trim"
              :items="trims"
              label="Trim *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col> -->

          <v-col cols="12">
            <v-select
              v-model="formData.selectedSection"
              :items="filteredSections"
              item-title="title"
              item-value="id"
              label="Trim *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
              :disabled="!formData.selectedGroup"
            ></v-select>
          </v-col>

          <!-- Regional Specs -->
          <v-col cols="12">
            <v-select
              v-model="formData.regionalSpecs"
              :items="regionalSpecs"
              label="Regional Specs *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Year -->
          <v-col cols="12">
            <v-select
              v-model="formData.year"
              :items="years"
              label="Year *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Kilometers -->
          <v-col cols="12">
            <v-text-field
              v-model="formData.kilometers"
              label="Kilometers *"
              :rules="[requiredRule, numberRule]"
              variant="outlined"
              dense
            ></v-text-field>
          </v-col>

          <!-- Body Type -->
          <v-col cols="12">
            <v-select
              v-model="formData.bodyType"
              :items="bodyTypes"
              label="Body Type *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Car Insurance -->
          <v-col cols="12">
            <v-select
              v-model="formData.insurance"
              :items="['Yes', 'No']"
              label="Is your car insured in EA region? *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Images Upload Field -->
          <v-col cols="12">
            <v-file-input
              v-model="formData.images"
              label="Upload Images (Max 5MB each, up to 10)"
              prepend-icon="mdi-camera"
              multiple
              show-size
              accept="image/*"
              :rules="[requiredRule, imagesRule]"
              variant="outlined"
              dense
              counter
              placeholder="Select car images"
              @change="onImagesChange"
            >
              <template #selection>
                <div v-if="formData.images && formData.images.length">
                  <v-chip
                    v-for="(file, i) in formData.images"
                    :key="i"
                    class="ma-1"
                    color="primary"
                    label
                    small
                  >
                    {{ file.name }}
                  </v-chip>
                </div>
                <span v-else class="grey--text">No images selected</span>
              </template>
            </v-file-input>
            <div v-if="imagesError" class="error--text">
              {{ imagesError }}
            </div>
          </v-col>

          <!-- Price with Currency -->
          <v-col cols="12">
            <v-row>
              <v-col cols="8">
                <v-text-field
                  v-model="formData.price"
                  label="Price *"
                  :rules="[requiredRule, numberRule, positivePriceRule]"
                  variant="outlined"
                  dense
                  type="number"
                  min="0"
                  placeholder="Enter price"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="formData.currency"
                  :items="currencies"
                  label="Currency"
                  :rules="[requiredRule]"
                  variant="outlined"
                  dense
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Description Field -->
          <v-col cols="12">
            <v-textarea
              v-model="formData.description"
              label="Describe Your Car *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
              placeholder="Include details about condition, modifications, and anything special about your car"
              counter="5000"
            ></v-textarea>
          </v-col>

          <!-- Fuel Type -->
          <v-col cols="12">
            <v-select
              v-model="formData.fuel_type"
              :items="fuelTypes"
              label="Fuel Type *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Doors -->
          <v-col cols="12">
            <v-select
              v-model="formData.doors"
              :items="doorOptions"
              label="Number of Doors *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Transmission Type -->
          <v-col cols="12">
            <v-select
              v-model="formData.transmission_type"
              :items="transmissionTypes"
              label="Transmission Type *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Seating Capacity -->
          <v-col cols="12">
            <v-select
              v-model="formData.seating_capacity"
              :items="seatingCapacities"
              label="Seating Capacity"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Horsepower -->
          <v-col cols="12">
            <v-select
              v-model="formData.horsepower"
              :items="horsepowerRanges"
              label="Horsepower *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Steering Side -->
          <v-col cols="12">
            <v-select
              v-model="formData.steering_side"
              :items="steeringSides"
              label="Steering Side *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Exterior Color -->
          <v-col cols="12">
            <v-select
              v-model="formData.exterior_color"
              :items="carColors"
              label="Exterior Color *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Interior Color -->
          <v-col cols="12">
            <v-select
              v-model="formData.interior_color"
              :items="carColors"
              label="Interior Color *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Warranty -->
          <v-col cols="12">
            <v-select
              v-model="formData.warranty"
              :items="warrantyOptions"
              label="Warranty *"
              :rules="[requiredRule]"
              variant="outlined"
              dense
            ></v-select>
          </v-col>

          <!-- Phone Number with Country Code -->
          <v-col cols="12">
            <v-text-field
              v-model="formData.phoneNumber"
              label="Phone Number (with country code, e.g. +252...) *"
              :rules="[requiredRule, phoneNumberRule]"
              variant="outlined"
              dense
              placeholder="+252612345678"
            />
          </v-col>

          <!-- Payment Amount (optional) -->
          <v-col cols="12">
            <v-text-field
              v-model="formData.payment_amount"
              label="Payment Amount (optional)"
              variant="outlined"
              dense
              type="number"
              min="0"
            />
          </v-col>

          <!-- Payment Phone Number (optional) -->
          <v-col cols="12">
            <div class="field-label">
              Please Pay a service fee of 50$ and Enter the Phone number Used
              for Payment
            </div>
            <v-text-field
              v-model="formData.payment_phone_number"
              placeholder="Enter phone number"
              :rules="[optionalPhoneNumberRule]"
              variant="outlined"
              dense
            />
          </v-col>

          <!-- 360 Tour URL -->
          <LearnMore360 />

          <!-- Car Technical Features Section -->
          <v-col cols="12">
            <h3 class="features-heading">Technical Features</h3>
            <v-row>
              <v-col
                v-for="(feature, index) in carFeatures"
                :key="index"
                cols="6"
                class="checkbox-column"
              >
                <v-checkbox
                  v-model="formData.technicalFeatures"
                  :value="feature.id"
                  :label="feature.name"
                  dense
                  class="compact-checkbox"
                  @change="updateTechnicalFeatures"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>

          <!-- Google Maps Section -->
          <v-col cols="12">
            <GoogleMapComponent
              ref="mapComponent"
              :cities="cities"
              :required="true"
              :height="'500px'"
              @locationSelected="handleLocationSelected"
              @locationCleared="handleLocationCleared"
              @error="handleMapError"
            />
            <div v-if="locationError" class="error--text mt-2">
              {{ locationError }}
            </div>
          </v-col>
        </v-row>

        <!-- Next Button -->
        <div
          class="sticky-footer-container"
          style="display: flex; flex-direction: column"
        >
          <div>
            <v-btn
              class="next-button"
              :disabled="!isFormValid || isSubmitting"
              @click="submitForm"
            >
              {{ isSubmitting ? "Submitting..." : "Next" }}
            </v-btn>
          </div>
          <div style="margin-top: 15px; text-align: center">
            Questions? Call <span class="contact-number">000 Suuq</span>
          </div>
        </div>
      </v-form>

      <!-- Reusable Loading Spinner -->
      <LoadingSpinner
        :active="isSubmitting"
        message="Submitting your car details..."
      />
    </v-container>
  </div>
</template>

<script setup>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import GoogleMapComponent from "@/components/GeneralListingSignupChildComponents/GoogleMapComponent.vue";
import LearnMore360 from "@/components/GeneralListingSignupChildComponents/LearnMore360.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue"; // Import the spinner
import ListingService from "@/services/ListingServicePostingEndpoints.js"; // Import the service
import { debugError, debugLog } from "@/utils/debugUtils";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router"; // Add router for navigation after submit
import { useStore } from "vuex";

const store = useStore();
const router = useRouter(); // Add router
const isSubmitting = ref(false); // Add state to track submission
const submitError = ref(null); // Track submission errors
const mapComponent = ref(null);
const locationError = ref("");
const selectedLocation = ref(null);

const filteredSections = ref([]);
const groups = computed(() => store.state.NewCategory.groups);

// Form data
// Update formData to include new fields
const formData = ref({
  cities: null,
  makeModels: null,
  trim: null,
   selectedGroup: null,  // Use this for group selection
  selectedSection: null, // Use this for section selection
  regionalSpecs: null,
  year: null,
  kilometers: null,
  bodyType: null,
  insurance: null,
  images: [],
  price: null,
  currency: "USD",
  phoneNumber: null,
  sectionId: null,
  groupId: null,
  payment_amount: null,
  payment_phone_number: null,
  // New fields:
  title: null,
  description: null,
  doors: null,
  transmission_type: null,
  seating_capacity: null,
  horsepower: null,
  steering_side: null,
  fuel_type: null,
  exterior_color: null,
  interior_color: null,
  warranty: null,
  tour_360_url: null,
  technicalFeatures: [],
});

const updateSectionsForSelectedGroup = (selectedGroup) => {
  if (selectedGroup && selectedGroup.sections) {
    filteredSections.value = selectedGroup.sections;

    // Store groupId in formData for submission
    formData.value.groupId = selectedGroup.id;
    formData.value.makeModels = selectedGroup.name;

    // Reset section selection when group changes
    formData.value.selectedSection = null;
    formData.value.sectionId = null;
  }
};

const valid = ref(false); // Tracks the validity of the form
const imagesError = ref("");
const currencies = ["USD", "KES", "SOS", "TZS", "UGX"];

// Add computed property to include map validation in form validity
const isFormValid = computed(() => {
  // Check if Vuetify form is valid
  if (!valid.value) return false;

  // Check if map location is selected (required)
  if (mapComponent.value) {
    const locationValidation = mapComponent.value.validateLocation();
    if (locationValidation !== true) return false;
  }

  // Check if images are valid
  if (imagesRule(formData.value.images) !== true) return false;

  return true;
});

// Add these new refs for form options
const doorOptions = ref([2, 3, 4, 5, "5+"]);
const transmissionTypes = ref([
  "Automatic",
  "Manual",
  "Semi-Automatic",
  "CVT",
  "DCT",
]);
const seatingCapacities = ref([2, 3, 4, 5, 6, 7, 8, "8+"]);
const horsepowerRanges = ref([
  "Under 100 hp",
  "100-150 hp",
  "150-200 hp",
  "200-250 hp",
  "250-300 hp",
  "300-350 hp",
  "350-400 hp",
  "400-500 hp",
  "Over 500 hp",
]);
const steeringSides = ref(["Left Hand Drive", "Right Hand Drive"]);
const fuelTypes = ref([
  "Petrol",
  "Diesel",
  "Hybrid",
  "Electric",
  "LPG",
  "CNG",
  "Other",
]);
const carColors = ref([
  "Black",
  "White",
  "Silver",
  "Gray",
  "Blue",
  "Red",
  "Green",
  "Yellow",
  "Brown",
  "Orange",
  "Purple",
  "Gold",
  "Beige",
  "Burgundy",
  "Champagne",
  "Charcoal",
  "Navy",
  "Bronze",
  "Copper",
  "Custom",
  "Other",
]);
const warrantyOptions = ref([
  "No Warranty",
  "Under Manufacturer Warranty",
  "Extended Warranty Available",
  "1 Year Dealer Warranty",
  "2 Year Dealer Warranty",
  "3+ Year Dealer Warranty",
  "Other",
]);

// Car technical features list
const carFeatures = ref([
  { id: 1, name: "Front Wheel Drive" },
  { id: 2, name: "Rear Wheel Drive" },
  { id: 3, name: "All Wheel Drive" },
  { id: 4, name: "TipTronic Gears" },
  { id: 5, name: "Anti-Lock Brakes/ABS" },
  { id: 6, name: "Cruise Control" },
  { id: 7, name: "DSG Gearbox" },
  { id: 8, name: "Parking Sensors" },
  { id: 9, name: "Rear Camera" },
  { id: 10, name: "Bluetooth" },
  { id: 11, name: "Navigation System" },
  { id: 12, name: "Sunroof" },
  { id: 13, name: "Leather Seats" },
  { id: 14, name: "Climate Control" },
  { id: 15, name: "Keyless Entry" },
  { id: 16, name: "Turbo Charged" },
]);

//computed property
const trims = computed(() => store.state.NewCategory.sections);
const regionalSpecs = computed(() => store.state.NewCategory.regionalSpecs);
const bodyTypes = computed(() => store.state.NewCategory.bodyTypes);
const years = ref(
  Array.from({ length: new Date().getFullYear() - 1990 + 1 }, (_, i) =>
    (1990 + i).toString()
  )
);

// Computed property for cities
// Computed property for cities
const cities = computed(() => {
  const rawCities = store.state.NewCategory.cities;
  console.log("Raw Cities Data:", rawCities); // Log raw data for debugging
  return rawCities; // Return the raw array directly
});

// Computed property for make models
const makeModels = computed(() => {
  const rawMakeModels = store.state.NewCategory.groups;
  console.log("Raw Make Models Data:", rawMakeModels); // Log raw data for debugging
  return rawMakeModels.map((model) => ({
    id: model.id,
    name: model.name,
    slug: model.slug,
  }));
});

// Validation rules
const requiredRule = (value) => !!value || "This field is required.";
const numberRule = (value) =>
  value === null || value === "" || isNaN(value)
    ? "This field must be a valid number."
    : true;
const positivePriceRule = (value) => value > 0 || "Price must be positive.";

// Image validation: max 10 images, each <= 5MB, only images
const imagesRule = (files) => {
  if (!files || !files.length) return "At least one image is required.";
  if (files.length > 10) return "You can upload up to 10 images.";
  for (const file of files) {
    if (!file.type.startsWith("image/")) return "Only image files are allowed.";
    if (file.size > 5 * 1024 * 1024) return `Image ${file.name} exceeds 5MB.`;
  }
  return true;
};
const onImagesChange = (files) => {
  imagesError.value = imagesRule(files) === true ? "" : imagesRule(files);
};

// Phone number validation for East Africa (E.164 format, +252, +254, +255, +256, +257, +211, +250, +251)
const phoneNumberRule = (value) => {
  if (!value) return "Phone number is required.";
  const regex = /^\+(252|254|255|256|257|211|250|251)[0-9]{7,12}$/;
  return regex.test(value)
    ? true
    : "Enter a valid East African phone number with country code.";
};
const optionalPhoneNumberRule = (value) => {
  if (!value) return true;
  return phoneNumberRule(value);
};

const navigateToHome = () => {
  router.push("/"); // Redirects to the home page
};

// Function to handle technical features updates
const updateTechnicalFeatures = () => {
  console.log("Technical features updated:", formData.value.technicalFeatures);
};

// Location handling methods
const handleLocationSelected = (location) => {
  selectedLocation.value = location;
  store.dispatch("NewCategory/selectLocation", location);

  // Update form data with location details
  formData.value.selectedLat = location.lat;
  formData.value.selectedLng = location.lng;
  formData.value.locationName =
    location.location?.name || location.formattedAddress || location.name;
  locationError.value = "";
};

const handleLocationCleared = () => {
  selectedLocation.value = null;
  store.dispatch("NewCategory/selectLocation", null);

  // Clear location fields
  delete formData.value.selectedLat;
  delete formData.value.selectedLng;
  delete formData.value.locationName;
  locationError.value = "";
};

const handleMapError = (error) => {
  console.error("Map error:", error);
  locationError.value = error;
};

// Update the preparePayload function
// Prepare payload for FormData (for images)
// Update preparePayload function to include all new fields
const preparePayload = (formData) => {
  const data = new FormData();

   console.log('Selected Section ID:', formData.selectedSection);
  console.log('Selected Group:', formData.selectedGroup);

  const selectedSectionObj = filteredSections.value.find(
    section => section.id === formData.selectedSection
  );

  // Existing fields
  data.append("categoryId", 1);
  data.append("subCategoryId", 1);
  data.append("groupId", formData.groupId || "");
  data.append("sectionId", formData.selectedSection || ""); // Use the ID directly
  data.append("userId", store.state.auth?.user?.id || "");
  data.append("cityId", formData.cities?.id || "");
  data.append("cityName", formData.cities?.name || "");

  // Location data from map component
  data.append(
    "selectedLat",
    formData.selectedLat || formData.cities?.lat || ""
  );
  data.append(
    "selectedLng",
    formData.selectedLng || formData.cities?.lng || ""
  );
  data.append(
    "locationName",
    formData.locationName || formData.cities?.name || ""
  );

  // data.append(
  //   "makeModel",
  //   formData.makeModels?.name || formData.makeModels?.slug || ""
  // );
  // data.append("trim", formData.trim || "");

  data.append(
    "makeModel",
    formData.makeModels || formData.selectedGroup?.name || ""
  );
    data.append("trim", selectedSectionObj?.title || formData.trim || "");
  data.append("specs", formData.regionalSpecs || "");
  data.append("year", formData.year || "");
  data.append("kilometers", formData.kilometers || "");
  data.append("bodyType", formData.bodyType || "");
  data.append("isInsured", formData.insurance === "Yes" ? 1 : 0);
  data.append("price", formData.price || "");
  data.append("currency", formData.currency || "USD");
  data.append("phoneNumber", formData.phoneNumber || "");

  // Title and description - new fields but prioritize custom values if provided
  data.append(
    "title",
    formData.title ||
      `${formData.makeModels?.name || ""} ${formData.year || ""}`
  );
  data.append(
    "description",
    formData.description ||
      `${formData.makeModels?.name || ""} ${formData.year || ""} - ${
        formData.trim || ""
      }`
  );

  // New fields
  data.append("doors", formData.doors || "");
  data.append("transmission_type", formData.transmission_type || "");
  data.append("seating_capacity", formData.seating_capacity || "");
  data.append("horsepower", formData.horsepower || "");
  data.append("steering_side", formData.steering_side || "");
  data.append("fuel_type", formData.fuel_type || "");
  data.append("exterior_color", formData.exterior_color || "");
  data.append("interior_color", formData.interior_color || "");
  data.append("warranty", formData.warranty || "");

  // Optional fields
  if (formData.tour_360_url) data.append("tour_360_url", formData.tour_360_url);
  if (formData.technicalFeatures && formData.technicalFeatures.length) {
    data.append(
      "technical_features",
      JSON.stringify(formData.technicalFeatures)
    );
  }


  if (formData.payment_amount)
    data.append("payment_amount", formData.payment_amount);
  if (formData.payment_phone_number)
    data.append("payment_phone_number", formData.payment_phone_number);

  // Images
  if (formData.images && formData.images.length) {
    formData.images.forEach((file) => {
      data.append("images[]", file);
    });
  }

  return data;
};

// Then update your submitForm function
// Submit form function
const submitForm = async () => {
  if (!valid.value) {
    debugError("Form validation failed");
    return;
  }

  // Validate map location
  if (mapComponent.value) {
    const locationValidation = mapComponent.value.validateLocation();
    if (locationValidation !== true) {
      console.error("Location validation failed:", locationValidation);
      locationError.value = locationValidation;
      return;
    }
  }

  if (imagesRule(formData.value.images) !== true) {
    imagesError.value = imagesRule(formData.value.images);
    return;
  }

  // Clear any previous error messages
  imagesError.value = "";
  locationError.value = "";

  try {
    isSubmitting.value = true; // Show spinner
    submitError.value = null;
    const payload = preparePayload(formData.value);
    const response = await ListingService.store(payload, 1, 1);
    debugLog("Car listing created successfully", response);
    router.push({
      name: "SuccessPage",
      params: { listingId: response.data.id },
    });
  } catch (error) {
    debugError("Error creating car listing", error);
    submitError.value =
      error.response?.data?.message ||
      "Failed to create listing. Please try again.";
  } finally {
    isSubmitting.value = false; // Hide spinner
  }
};

// Fetch data on mount
onMounted(() => {
  const carSubcategoryId = 1;

  store.dispatch("NewCategory/fetchGroups", carSubcategoryId).then(() => {
    console.log("Fetched Groups Structure:", store.state.NewCategory.groups);
    // Optional: Log each group's structure in detail
  });

  store.dispatch("NewCategory/fetchSections"); // Fetch sections for cars (categoryId = 1)
  store.dispatch("NewCategory/fetchRegionalSpecs");
  store.dispatch("NewCategory/fetchBodyTypes");
  store.dispatch("NewCategory/fetchCities").then(() => {
    console.log("Formatted Cities Data:", cities.value); // Log formatted cities
    formData.city = "mogadishu"; // Default to Mogadishu
  });
  // store.dispatch('NewCategory/fetchGroups').then(() => {
  //   console.log('Formatted Make Models Data:', makeModels.value); // Log formatted make models
  // });

  // Initialize location state
  selectedLocation.value = null;
  store.dispatch("NewCategory/selectLocation", null);
});
</script>
<style scoped>
/* Add styling for the car-signup-form container */
.car-signup-form {
  min-height: 100vh;
  background-color: var(
    --white-pearl
  ); /* Using CSS variable from white theme */
  display: flex;
  flex-direction: column;
}

html,
body {
  height: 100%;
  overflow-x: hidden;
}
/* App Bar Styling */
.app-bar {
  background-color: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.logo-img {
  height: 60px;
  width: auto;
  cursor: pointer;
}

/* Content Container */
.content-container {
  max-width: 40%; /* Centered content with 30% margins on each side */
  margin: 0 auto;
  padding: 24px;
}

/* Title Card */
.title-card {
  margin-bottom: 16px;
}

.title {
  font-size: 22px;
  font-weight: bold;
  text-align: center;
}
.breadcrumbs {
  margin-bottom: 4px;
  font-weight: bold;
  font-size: 14px;
  color: #000; /* Black color for Home icon */
  transition: color 0.3s ease;
  text-align: var(--breadcrumbs-align, left); /* Default to left alignment */
  width: auto; /* Adjust width to fit content */
  padding-left: 10; /* Remove extra padding */
  display: flex; /* Ensure proper alignment */
  align-items: flex-start; /* Vertically align items */
  margin-left: 10;
  justify-content: flex-start !important;
}
.breadcrumbs :deep(.v-breadcrumbs-item:hover) {
  color: darkgoldenrod; /* Deep red on hover */
}

/* Info Card */
.info-card {
  background-color: #f0f8ff;
  margin-bottom: 16px;
  padding: 6px;
  border-radius: 8px;
}

.info-title {
  font-weight: bold;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.info-subtitle {
  font-size: 16px;
  letter-spacing: 0.3px;
  color: #333;
}

.learn-more {
  color: #000080;
  text-decoration: none;
  cursor: pointer;
  font-weight: 700;
}

.form-card:hover {
  border-color: black; /* Darker border on hover */
}

/* Next Button */
.next-button {
  width: 100%;
  background-color: darkgoldenrod; /* Always red */
  color: white;
  font-weight: bold;
  padding: 16px; /* Increased height */
  border-radius: 8px;
  margin-top: 16px;
  transition: background-color 0.3s ease; /* Smooth transition */
}

.next-button:hover {
  background-color: darkgoldenrod; /* Slightly darker red on hover */
}

/* Footer Card */
.footer-card {
  position: sticky;
  margin: 16px 0 0 0;
  padding: 8px 0;
}

.footer-text {
  font-size: 14px;
}

.contact-number {
  font-weight: semi-bold;
  color: #000080;
}

/* Add to existing styles */
.features-heading {
  font-size: 18px;
  font-weight: bold;
  margin-top: 8px;
  margin-bottom: 16px;
}

/* Field label styling */
.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  display: block;
}

.checkbox-column {
  padding: 4px;
}

.compact-checkbox {
  margin-top: 0;
  margin-bottom: 0;
}

.help-text {
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 8px;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* Full-width container on mobile with minimal padding */
  .content-container {
    max-width: 100%;
    width: 100%;
    padding: 0;
  }

  /* Remove box shadow and border radius for full bleed appearance */
  .title-card,
  .info-card,
  .form-card,
  .footer-card {
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 12px; /* Reduce spacing between cards */
  }

  /* Left-align and reduce title size */
  .title {
    font-size: 18px;
    text-align: left;
    padding: 16px 16px 0;
  }

  /* Adjust breadcrumbs */
  :deep(.v-breadcrumbs) {
    padding: 0 16px 8px;
    margin-bottom: 0;
  }

  /* Style info card to match screenshot */
  .info-card {
    padding: 8px 16px;
    margin-bottom: 16px;
    border-radius: 0;
  }

  .info-title {
    font-size: 16px;
  }

  .info-subtitle {
    font-size: 14px;
  }

  /* Form field adjustments */
  .form-card {
    margin-bottom: 12px;
    border-left: 0;
    border-right: 0;
    border-radius: 0;
  }

  /* Add padding to form row */
  .v-form .v-row {
    margin: 0;
    padding: 0 16px;
  }

  /* Form field text size */
  :deep(.v-text-field .v-label),
  :deep(.v-select .v-label) {
    font-size: 14px;
  }

  /* Next button styling for mobile */
  .next-button {
    margin: 16px;
    width: calc(100% - 32px); /* Full width minus padding */
    padding: 12px;
    border-radius: 4px;
  }

  /* Footer card adjustments */
  .footer-card {
    margin: 16px 0 0 0;
    padding: 8px 0;
  }

  /* Logo adjustments */
  .logo-img {
    height: 45px !important;
  }

  /* App bar height adjustment */
  .app-bar {
    height: 56px;
  }
}

.sticky-footer-container {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 12px 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);

  /* Match the form width */
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
}

/* iPhone-specific adjustments */
@media (max-width: 390px) {
  .info-title {
    font-size: 15px;
  }

  .info-subtitle {
    font-size: 13px;
  }

  :deep(.v-text-field .v-label),
  :deep(.v-select .v-label) {
    font-size: 13px;
  }

  .car-title {
    text-align: center;
  }
}
</style>
